import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, MapPin } from "lucide-react";
import { BRANCHES } from "../../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < n ? "fill-[#FACC15] text-[#FACC15]" : "text-white/15"
        }`}
      />
    ))}
  </div>
);

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && reviews.length === 0) return null;

  const avg =
    reviews.length > 0
      ? (
          reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length
        ).toFixed(1)
      : "5.0";

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-[#0F0F0F]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-[#FACC15] font-semibold mb-4">
              Student Reviews
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white leading-[1.05]">
              Loved by Noida&apos;s
              <br />
              <span className="text-[#FACC15] italic font-light">
                serious students.
              </span>
            </h2>
          </div>

          <div className="flex items-center gap-5 bg-[#1A1A1A] border border-white/5 rounded-sm p-5">
            <div>
              <div className="font-display text-4xl font-bold text-[#FACC15] leading-none">
                {avg}
              </div>
              <Stars n={5} />
            </div>
            <div className="border-l border-white/10 pl-5 leading-tight">
              <div className="text-sm text-white font-semibold">
                {reviews.length}+ five-star reviews
              </div>
              <div className="text-xs text-white/50">
                From real Justudy students
              </div>
            </div>
          </div>
        </div>

        <div
          data-testid="reviews-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
        >
          {reviews.map((r, i) => (
            <article
              key={r.id || i}
              data-testid={`review-card-${i}`}
              className="relative bg-[#1A1A1A] hover:bg-[#211E1A] border border-white/5 hover:border-[#FACC15]/30 rounded-sm p-6 md:p-7 transition-all group"
            >
              <Quote className="absolute top-5 right-5 w-8 h-8 text-[#FACC15]/10 group-hover:text-[#FACC15]/30 transition-colors" />
              <Stars n={r.rating || 5} />
              <p className="mt-4 text-sm md:text-[15px] text-white/80 leading-relaxed">
                &ldquo;{r.message}&rdquo;
              </p>
              <div className="mt-6 pt-5 border-t border-white/5 flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-base font-semibold text-white">
                    {r.name}
                  </div>
                  {r.role && (
                    <div className="text-xs text-white/45 mt-0.5">{r.role}</div>
                  )}
                </div>
                {r.branch && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#FACC15] border border-[#FACC15]/25 px-2 py-1 rounded-sm whitespace-nowrap">
                    <MapPin className="w-3 h-3" />
                    {r.branch.replace(" Noida", "")}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-center">
          <span className="text-sm text-white/50">
            Have a great experience? Leave us a Google review:
          </span>
          {Object.values(BRANCHES).map((b) => (
            <a
              key={b.id}
              href={b.reviewLink || b.mapsLink}
              target="_blank"
              rel="noreferrer"
              data-testid={`review-write-${b.id}`}
              className="inline-flex items-center gap-1.5 text-xs border border-white/15 hover:border-[#FACC15] hover:text-[#FACC15] text-white/80 px-3 py-2 rounded-sm transition-colors font-semibold"
            >
              <Star className="w-3.5 h-3.5 text-[#FACC15] fill-[#FACC15]" />
              Review {b.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
